// REQ-1 · Componente "línea de estados" (hitos visuales) — tarea 13.
// Responsive: horizontal en desktop; vertical (círculo a la izquierda, detalle a la derecha) en móvil.
// Cada hito "posee" el conector hacia el siguiente, para que la línea funcione en ambas orientaciones.
// Se usa una instancia por cada línea (siniestro / vehículo).

import { Link } from 'react-router-dom';
import {
  CalendarOutlined,
  CarOutlined,
  CheckOutlined,
  CloseOutlined,
  DollarOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  KeyOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { formatDate } from '../../helpers/format-date';
import { Milestone, MilestoneIcon } from '../../Interfaces/ClaimStatus.interface';
import './StatusTimeline.scss';

type Props = {
  milestones: Milestone[];
  /**
   * Rótulo de la línea. La vista muestra dos líneas seguidas y sin él no se distingue
   * cuál es cuál: el cliente veía dos rieles de hitos sin saber que el de abajo es el
   * taller. Se usa además como nombre accesible del grupo.
   */
  title?: string;
};

const ICONS: Record<MilestoneIcon, JSX.Element> = {
  file: <FileTextOutlined />,
  'file-done': <FileDoneOutlined />,
  dollar: <DollarOutlined />,
  calendar: <CalendarOutlined />,
  car: <CarOutlined />,
  tool: <ToolOutlined />,
  key: <KeyOutlined />,
};

// El link de redirección se muestra sólo si el hito tiene acción y aún no está completado.
const ActionLink = ({ action }: { action: NonNullable<Milestone['action']> }) => {
  const isExternal = /^https?:/i.test(action.href);
  if (isExternal) {
    return (
      <a className="status-timeline__action" href={action.href} target="_blank" rel="noopener noreferrer">
        {action.label}
      </a>
    );
  }
  return (
    <Link className="status-timeline__action" to={action.href}>
      {action.label}
    </Link>
  );
};

type NodeProps = {
  milestone: Milestone;
  showConnector: boolean;
  connectorActive: boolean;
};

const Node = ({ milestone, showConnector, connectorActive }: NodeProps) => {
  const { status } = milestone;
  const dateText = formatDate(milestone.date);
  const captionText = dateText || milestone.caption || '';
  const showAction = !!milestone.action && status !== 'done';

  return (
    <div className={`status-timeline__node status-timeline__node--${status}`}>
      <div className="status-timeline__marker">
        <div className="status-timeline__circle">
          {ICONS[milestone.icon]}
          {status === 'done' && (
            <span className="status-timeline__badge status-timeline__badge--ok">
              <CheckOutlined />
            </span>
          )}
          {status === 'na' && (
            <span className="status-timeline__badge status-timeline__badge--na">
              <CloseOutlined />
            </span>
          )}
        </div>
        {showConnector && <span className={`status-timeline__connector ${connectorActive ? 'status-timeline__connector--active' : ''}`} />}
      </div>

      <div className="status-timeline__content">
        <div className="status-timeline__label">{milestone.label}</div>
        {captionText && (
          <div className={`status-timeline__caption ${dateText ? 'status-timeline__caption--date' : status === 'na' ? 'status-timeline__caption--muted' : 'status-timeline__caption--strong'}`}>
            {/* El rótulo va en gris tenue y la fecha conserva su peso: explica el dato sin competir con él. */}
            {dateText && milestone.dateLabel && (
              <span className="status-timeline__caption-label">{milestone.dateLabel}: </span>
            )}
            {captionText}
          </div>
        )}
        {showAction && <ActionLink action={milestone.action!} />}
      </div>
    </div>
  );
};

export const StatusTimeline = ({ milestones, title }: Props) => {
  // Defensa: si la respuesta no trae la línea de hitos (o no es un arreglo), no renderizar
  // en lugar de reventar el render (evita la "pantalla en negro").
  const list = Array.isArray(milestones) ? milestones : [];
  if (list.length === 0) return null;
  const lastIndex = list.length - 1;
  // El id se deriva del primer hito: es estable y único entre las dos líneas de la vista.
  const titleId = title ? `status-timeline-${list[0].code}` : undefined;
  return (
    <div className="status-timeline" role="group" aria-labelledby={titleId}>
      {title && (
        <p className="status-timeline__title" id={titleId}>
          {title}
        </p>
      )}
      <div className="status-timeline__track">
        {list.map((m, i) => (
          // El conector que sale de un hito está "activo" si ese hito ya está completado.
          <Node key={m.code} milestone={m} showConnector={i < lastIndex} connectorActive={m.status === 'done'} />
        ))}
      </div>
    </div>
  );
};

export default StatusTimeline;
